import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../hooks/useApi.js';

interface Deelnemer { id: string; email: string; naam: string; rol: string; hoofdklant: boolean; status: string }

const STATUS: Record<string, string> = { uitgenodigd: 'Uitgenodigd', geaccepteerd: 'Geaccepteerd', geweigerd: 'Geweigerd' };

/**
 * Winkelkant, minimaal: een klant uitnodigen voor /mijn en een uitnodiging intrekken. Het volledige
 * deelnemersbeheer (opnieuw uitnodigen, rollen, de knop Portaallink vervangen) is fase 2.
 */
export function KlantUitnodigenKnop({ dossierId, className }: { dossierId: string; className?: string }) {
  const [open, setOpen] = useState(false);
  const [deelnemers, setDeelnemers] = useState<Deelnemer[]>([]);
  const [naam, setNaam] = useState('');
  const [email, setEmail] = useState('');
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState('');
  const [fout, setFout] = useState('');

  const laad = useCallback(() => {
    apiFetch<Deelnemer[]>(`/api/dossier-deelnemers/${encodeURIComponent(dossierId)}`).then(setDeelnemers).catch(() => setFout('De deelnemers konden niet geladen worden.'));
  }, [dossierId]);
  useEffect(() => { if (open) laad(); }, [open, laad]);

  const nodigUit = async () => {
    setBezig(true); setFout(''); setMelding('');
    try {
      const r = await apiFetch<{ mailVerstuurd: boolean }>(`/api/dossier-deelnemers/${encodeURIComponent(dossierId)}`, {
        method: 'POST', body: JSON.stringify({ naam, email }),
      });
      setMelding(r.mailVerstuurd ? `Uitnodiging verstuurd naar ${email}.` : `Uitnodiging klaargezet, maar de mail vertrok niet. Controleer het adres of de mailinstellingen.`);
      setNaam(''); setEmail('');
      laad();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'De uitnodiging lukte niet.');
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (d: Deelnemer) => {
    setFout(''); setMelding('');
    try {
      await apiFetch(`/api/dossier-deelnemers/${encodeURIComponent(dossierId)}/${encodeURIComponent(d.id)}`, { method: 'DELETE' });
      setMelding(`${d.naam} heeft geen toegang meer.`);
      laad();
    } catch {
      setFout('Verwijderen lukte niet.');
    }
  };

  return (
    <div className={className}>
      <button type="button" className="k-button k-button--secondary" aria-expanded={open} onClick={() => setOpen((v) => !v)}>Klant uitnodigen</button>
      {open && (
        <div className="mt-3 space-y-3">
          {deelnemers.length > 0 && (
            <ul className="space-y-1">
              {deelnemers.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span>{d.naam} · {d.email}{d.hoofdklant ? ' · hoofdklant' : ''} · {STATUS[d.status] ?? d.status}</span>
                  <button type="button" className="k-link min-h-[44px]" onClick={() => verwijder(d)}>Verwijderen<span className="k-sr-only"> {d.naam}</span></button>
                </li>
              ))}
            </ul>
          )}
          <div>
            <label htmlFor="uitnodig-naam" className="k-label block mb-1">Naam</label>
            <input id="uitnodig-naam" className="k-field w-full" value={naam} onChange={(e) => setNaam(e.target.value)} />
          </div>
          <div>
            <label htmlFor="uitnodig-email" className="k-label block mb-1">E-mailadres</label>
            <input id="uitnodig-email" type="email" className="k-field w-full" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button type="button" className="k-button" disabled={bezig || !naam.trim() || !email.trim()} aria-busy={bezig || undefined} onClick={nodigUit}>
            {bezig ? 'Bezig…' : 'Uitnodiging versturen'}
          </button>
          <p aria-live="polite" className="text-[13px] leading-5 text-[var(--k-muted)]">{melding}</p>
          {fout && <p role="alert" className="text-[13px] leading-5 text-[var(--k-danger)]">{fout}</p>}
        </div>
      )}
    </div>
  );
}
